import { fetchWithAuth } from "../lib/api";

export type MachineEventStatus = "executed" | "failed" | "cancelled" | "rate_limited";

export interface OperatorConsoleSummary {
  generated_at: string;
  operator_triggered_events: Array<{
    id: string;
    label: string;
    command: string;
    status: MachineEventStatus;
    occurred_at: string | null;
    source: "tdc_command_log";
    provenance: "operator_triggered";
  }>;
  shortlist_next_actions: Array<{
    id: string;
    client_id: string;
    title: string;
    description: string | null;
    priority: "urgent" | "high" | "normal" | "low";
    due_at: string | null;
    source: "client_shortlist_outcome";
  }>;
  health: {
    director_channel: {
      state: "healthy" | "no_recent_data";
      last_event_at: string | null;
      source: string;
      freshness_rule: string;
    };
  };
  spend: {
    anthropic_nlu: AvailableSpend;
    twilio_lookup: AvailableSpend & { today_calls: number };
    twilio_messaging: { state: "unavailable"; reason: string; source: "none" };
  };
}

export interface AvailableSpend {
  state: "available";
  today_usd: number;
  seven_day_usd: number;
  daily_cap_usd: number;
  source: string;
  window: string;
  through: string;
  completeness: string;
  measurement_kind: "recorded_actual" | "conservative_cap_accounting_estimate";
  pricing_basis?: string;
}

const isRecord = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

const isMachineEvent = (
  value: unknown
): value is OperatorConsoleSummary["operator_triggered_events"][number] =>
  isRecord(value) &&
  ["id", "label", "command"].every((key) => typeof value[key] === "string") &&
  ["executed", "failed", "cancelled", "rate_limited"].includes(value.status) &&
  isNullableString(value.occurred_at) &&
  value.source === "tdc_command_log" &&
  value.provenance === "operator_triggered";

const isShortlistAction = (
  value: unknown
): value is OperatorConsoleSummary["shortlist_next_actions"][number] =>
  isRecord(value) &&
  ["id", "client_id", "title"].every((key) => typeof value[key] === "string") &&
  isNullableString(value.description) &&
  ["urgent", "high", "normal", "low"].includes(value.priority) &&
  isNullableString(value.due_at) &&
  value.source === "client_shortlist_outcome";

const isAvailableSpend = (value: unknown): value is AvailableSpend =>
  isRecord(value) &&
  value.state === "available" &&
  ["today_usd", "seven_day_usd", "daily_cap_usd"].every(
    (key) => typeof value[key] === "number" && Number.isFinite(value[key])
  ) &&
  ["source", "window", "through", "completeness"].every(
    (key) => typeof value[key] === "string"
  ) &&
  ["recorded_actual", "conservative_cap_accounting_estimate"].includes(
    value.measurement_kind
  ) &&
  (value.pricing_basis === undefined || typeof value.pricing_basis === "string");

const isLookupSpend = (
  value: unknown
): value is AvailableSpend & { today_calls: number } => {
  if (!isAvailableSpend(value)) return false;
  const todayCalls = (value as { today_calls?: unknown }).today_calls;
  return typeof todayCalls === "number" && Number.isFinite(todayCalls);
};

export const isOperatorConsoleSummary = (
  value: unknown
): value is OperatorConsoleSummary => {
  if (!isRecord(value) || !isRecord(value.health) || !isRecord(value.spend)) {
    return false;
  }
  const director = value.health.director_channel;
  const messaging = value.spend.twilio_messaging;
  return (
    typeof value.generated_at === "string" &&
    Array.isArray(value.operator_triggered_events) &&
    value.operator_triggered_events.every(isMachineEvent) &&
    Array.isArray(value.shortlist_next_actions) &&
    value.shortlist_next_actions.every(isShortlistAction) &&
    isRecord(director) &&
    (director.state === "healthy" || director.state === "no_recent_data") &&
    isNullableString(director.last_event_at) &&
    typeof director.source === "string" &&
    typeof director.freshness_rule === "string" &&
    isAvailableSpend(value.spend.anthropic_nlu) &&
    isLookupSpend(value.spend.twilio_lookup) &&
    isRecord(messaging) &&
    messaging.state === "unavailable" &&
    typeof messaging.reason === "string" &&
    messaging.source === "none"
  );
};

export const fetchOperatorConsole = async (): Promise<OperatorConsoleSummary> => {
  const result = await fetchWithAuth<unknown>("/admin/operator-console");
  if (!isOperatorConsoleSummary(result)) {
    throw new Error("Operator console returned an invalid response");
  }
  return result;
};
